import { useCallback, useState, useEffect, useRef } from "react";
import { useUserContext } from "../../contexts/UserContext";
import { usePusher } from "../../contexts/PusherContext";
import { catchError } from "../../services/DaoService";
import { Button, Spin, Empty, Avatar, Tooltip, Modal } from "antd";
import { EditFilled, DeleteFilled, ReloadOutlined } from "@ant-design/icons";
import { CommentaireService } from "../../services/CommentaireService";
import UserService from "../../services/UserService";
import CommentaireCreateForm from "./CommentaireCreateForm";
import CommentaireEditForm from "./CommentaireEditForm";
import moment from "moment";
import PropTypes from "prop-types";
import styles from "./CommentaireFeed.module.scss";

// Parse @email mentions and render name + avatar chips as React nodes
function CommentText({ text, users, isMine }) {
  // Split on @email patterns
  const parts = [];
  const regex = /@([\w.+%-]+@[\w.-]+\.[a-zA-Z]{2,})/g;
  let last = 0;
  let match;
  const plain = text.replace(/\n/g, '\n');

  while ((match = regex.exec(plain)) !== null) {
    if (match.index > last) {
      parts.push({ type: 'text', value: plain.slice(last, match.index) });
    }
    const email = match[1];
    const user = users.find(u => u.email === email);
    parts.push({ type: 'mention', email, user });
    last = match.index + match[0].length;
  }
  if (last < plain.length) parts.push({ type: 'text', value: plain.slice(last) });

  return (
    <div className={`${styles.bubbleText} ${isMine ? styles.bubbleTextMine : ''}`}>
      {parts.map((p, i) => {
        if (p.type === 'text') {
          return <span key={i}>{p.value.split('\n').map((line, j, arr) => (
            <span key={j}>{line}{j < arr.length - 1 && <br />}</span>
          ))}</span>;
        }
        const name = p.user?.name ?? p.email;
        const avatar = p.user?.avatar;
        return (
          <span key={i} className={styles.mentionChip}>
            <Avatar size={14} src={avatar}>{name[0]}</Avatar>
            <strong>{name}</strong>
          </span>
        );
      })}
    </div>
  );
}

CommentText.propTypes = {
  text: PropTypes.string.isRequired,
  users: PropTypes.array.isRequired,
  isMine: PropTypes.bool,
};

export default function CommentaireFeed({ parentType, parentId, isReadOnly = false, canCreate = false }) {
  const [commentaires, setCommentaires] = useState([]);
  const [paginationData, setPaginationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [users, setUsers] = useState([]);
  const { currentUser } = useUserContext();
  const { pusher } = usePusher();
  const threadRef = useRef(null);

  useEffect(() => {
    UserService.getInstance().all().then(r => setUsers(r.data ?? [])).catch(catchError);
  }, []);

  const scrollToBottom = () => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const init = useCallback(() => {
    setLoading(true);
    CommentaireService.getInstance()
      .findByParent(parentId, parentType)
      .then(r => {
        setPaginationData(r.data);
        setCommentaires(r.data.data);
        setTimeout(scrollToBottom, 50);
      })
      .catch(catchError)
      .finally(() => setLoading(false));
  }, [parentType, parentId]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    CommentaireService.getInstance()
      .findByParent(parentId, parentType)
      .then(r => {
        setPaginationData(r.data);
        setCommentaires(r.data.data);
        setTimeout(scrollToBottom, 50);
      })
      .catch(catchError)
      .finally(() => setRefreshing(false));
  }, [parentType, parentId]);

  useEffect(() => { init(); }, [init]);

  // Abonnement au canal temps réel du parent (Affaire/Tache) : pousse les
  // commentaires créés par n'importe quel utilisateur, sans recharger la page.
  useEffect(() => {
    if (!pusher || !parentType || !parentId) return;

    const shortType = parentType.split('\\').pop();
    const channelName = `private-commentaires.${shortType}.${parentId}`;
    const channel = pusher.subscribe(channelName);
    console.log(`Subscribed to Pusher channel ${channelName}`);

    const handleNewCommentaire = (data) => {
      setCommentaires(prev => {
        if (prev.some(c => c.uid === data.uid)) return prev; // déjà ajouté (ex: auteur lui-même)
        return [...prev, data];
      });
      setPaginationData(prev => prev ? { ...prev, total: (prev.total ?? 0) + 1 } : prev);
      setTimeout(scrollToBottom, 50);
    };

    channel.bind('new-commentaire', handleNewCommentaire);

    return () => {
      channel.unbind('new-commentaire', handleNewCommentaire);
      pusher.unsubscribe(channelName);
    };
  }, [pusher, parentType, parentId]);

  const loadMore = () => {
    if (!paginationData?.next_page_url || loadingMore) return;
    setLoadingMore(true);
    CommentaireService.post(paginationData.next_page_url, { parent_type: parentType })
      .then(r => { setPaginationData(r.data); setCommentaires(prev => [...r.data.data, ...prev]); })
      .catch(catchError)
      .finally(() => setLoadingMore(false));
  };

  const handleCreated = (newComment) => {
    setCommentaires(prev => [...prev, newComment]);
    setPaginationData(prev => prev ? { ...prev, total: (prev.total ?? 0) + 1 } : prev);
    setTimeout(scrollToBottom, 50);
  };

  const handleDelete = (uid) => {
    CommentaireService.getInstance()
      .remove(uid)
      .then(() => {
        setCommentaires(prev => prev.filter(c => c.uid !== uid));
        setPaginationData(prev => prev ? { ...prev, total: Math.max(0, (prev.total ?? 1) - 1) } : prev);
      })
      .catch(catchError);
  };

  const handleUpdate = (updated) => {
    setCommentaires(prev => prev.map(c => c.uid === updated.uid ? updated : c));
    setEditModal(null);
  };

  return (
    <div className={styles.section}>
      <div className={styles.loadMoreBar}>
        <Button
          size="small"
          onClick={handleRefresh}
          loading={refreshing}
          icon={<ReloadOutlined />}
          type="link"
        >
          Rafraîchir
        </Button>
      </div>

      {paginationData?.next_page_url && (
        <div className={styles.loadMoreBar}>
          <Button size="small" onClick={loadMore} loading={loadingMore} type="link">
            Charger les précédents
          </Button>
        </div>
      )}

      <Spin spinning={loading}>
        <div className={styles.thread} ref={threadRef}>
          {commentaires.length === 0 && !loading && (
            <div className={styles.empty}>
              <Empty description="Aucun commentaire" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )}
          {commentaires.map(item => {
            const isMine = item.user_id === currentUser?.id;
            const canEdit = !isReadOnly && isMine;
            const canDelete = !isReadOnly && isMine;
            const ts = item.edited
              ? moment(item.updated_at).fromNow()
              : moment(item.created_at).fromNow();

            return (
              <div key={item.uid} className={`${styles.message} ${isMine ? styles.mine : styles.theirs}`}>
                {!isMine && (
                  <Tooltip title={item.user?.name}>
                    <Avatar size={30} src={item.user?.avatar} className={styles.avatar}>
                      {item.user?.name?.[0]}
                    </Avatar>
                  </Tooltip>
                )}
                <div className={styles.bubble}>
                  {!isMine && (
                    <span className={styles.bubbleAuthor}>{item.user?.name}</span>
                  )}
                  <CommentText text={item.comment} users={users} isMine={isMine} />
                  <div className={styles.bubbleMeta}>
                    <span className={styles.bubbleTime}>{ts}</span>
                    {!!item.edited && <span className={styles.edited}>(modifié)</span>}
                    {(canEdit || canDelete) && (
                      <span className={styles.bubbleActions}>
                        {canEdit && (
                          <Tooltip title="Modifier">
                            <Button type="text" size="small" icon={<EditFilled />} className={styles.actionBtn} onClick={() => setEditModal(item)} />
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Supprimer">
                            <Button
                              type="text" size="small" danger icon={<DeleteFilled />} className={styles.actionBtn}
                              onClick={() => Modal.confirm({
                                title: 'Supprimer ce commentaire ?',
                                okText: 'Supprimer', cancelText: 'Annuler', okType: 'danger',
                                onOk: () => handleDelete(item.uid),
                              })}
                            />
                          </Tooltip>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Spin>

      {canCreate && (
        <CommentaireCreateForm
          target={parentType}
          parentId={parentId}
          onCreate={handleCreated}
          users={users}
        />
      )}

      {editModal && (
        <CommentaireEditForm
          visible={!!editModal}
          uid={editModal.uid}
          onUpdate={handleUpdate}
          onCancel={() => setEditModal(null)}
        />
      )}
    </div>
  );
}

CommentaireFeed.propTypes = {
  parentType: PropTypes.string.isRequired,
  parentId: PropTypes.number.isRequired,
  isReadOnly: PropTypes.bool,
  canCreate: PropTypes.bool,
};
